import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '../components/icons';
import { BrandName, Mark } from '../components/Layout';
import { LoginScene } from '../components/LoginScene';
import { PhoneField } from '../components/PhoneField';
import { ErrorNote, Field } from '../components/ui';
import { post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toAsciiDigits } from '../lib/format';
import { useTenant } from '../lib/tenant';
import { isNativeApp, nativeHome } from '../lib/app';

type Choice = { ticket: string; schoolName: string; schoolNameAr: string | null; role: string };

/** Language button with a globe, as on the sign-in page */
export function GlobeLanguage() {
  const { i18n } = useTranslation();
  const next = i18n.language === 'ar' ? 'en' : 'ar';
  return (
    <button type="button" className="lang-switch lang-globe" lang={next} onClick={() => void i18n.changeLanguage(next)}>
      <Icon name="globe" size={20} />
      {next === 'ar' ? 'العربية' : 'English'}
    </button>
  );
}

export function Login({ note }: { note?: string }) {
  const { t, i18n } = useTranslation();
  const { refresh } = useAuth();
  const tenant = useTenant();
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const [mode, setMode] = useState<'phone' | 'email' | 'qr'>('phone');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let r: { choose?: Choice[] };
      if (mode === 'email') r = await post('/api/auth/login', { email, password, locale: i18n.language });
      else if (!codeSent) {
        await post('/api/auth/otp/request', { phone });
        setCodeSent(true);
        return;
      } else r = await post('/api/auth/otp/verify', { phone, code, locale: i18n.language });
      // The same person at several schools: ask which one
      if (r.choose) return setChoices(r.choose);
      await refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const pick = async (c: Choice) => {
    setBusy(true);
    setError(null);
    try {
      await post('/api/auth/pick', { ticket: c.ticket, locale: i18n.language });
      await refresh();
    } catch (err) {
      setError(err);
      setChoices(null);
    } finally {
      setBusy(false);
    }
  };

  const ar = i18n.language === 'ar';
  const school = tenant.school;
  const schoolName = school ? (ar && school.nameAr) || school.name : null;
  const switchMode = (m: 'phone' | 'email' | 'qr') => {
    setMode(m);
    setError(null);
  };

  return (
    <div className="login login-v2">
      <LoginScene />
      <div className="login-lang">
        <GlobeLanguage />
      </div>

      <section className="login-card">
        <div className="login-brand login-brand-center">
          {school?.logoUrl ? <img className="login-logo" src={school.logoUrl} alt="" /> : <Mark />}
          <h1>{schoolName ?? <BrandName name={t('app.name')} />}</h1>
        </div>
        <p className="login-tagline">{schoolName ? tenant.platformName : t('app.tagline')}</p>
        <p className="login-lede">{note ?? t('auth.lede')}</p>

        {choices ? (
          <div className="stack">
            <h2>{t('auth.chooseSchool')}</h2>
            <p className="muted">{t('auth.chooseHint')}</p>
            {choices.map((c) => (
              <button key={c.ticket} type="button" className="btn btn-block choice" disabled={busy} onClick={() => void pick(c)}>
                <span>{(ar && c.schoolNameAr) || c.schoolName}</span>
                <span className="muted small">{t(`role.${c.role}`)}</span>
              </button>
            ))}
            <button type="button" className="btn-quiet" onClick={() => setChoices(null)}>
              {t('common.back')}
            </button>
          </div>
        ) : mode === 'qr' ? (
          <QrSignIn onBack={() => switchMode('phone')} onDone={refresh} />
        ) : (
          <>
            <div className="segmented login-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={mode === 'phone'} onClick={() => switchMode('phone')}>
                <Icon name="phone" size={20} />
                {t('auth.withPhone')}
              </button>
              <button type="button" role="tab" aria-selected={mode === 'email'} onClick={() => switchMode('email')}>
                <Icon name="mail" size={20} />
                {t('auth.withEmail')}
              </button>
            </div>

            <form onSubmit={submit} className="stack">
              {mode === 'email' ? (
                <>
                  <Field label={t('auth.email')}>
                    <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" />
                  </Field>
                  <Field label={t('auth.password')}>
                    <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" />
                  </Field>
                </>
              ) : (
                <>
                  <Field label={t('auth.phone')}>
                    <PhoneField
                      required
                      onChange={(v) => {
                        if (v !== phone) setCodeSent(false);
                        setPhone(v);
                      }}
                    />
                  </Field>
                  {codeSent && (
                    <Field label={t('auth.code')} hint={t('auth.codeSent')}>
                      <input inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required value={code} onChange={(e) => setCode(toAsciiDigits(e.target.value).replace(/\D/g, ''))} dir="ltr" className="code-input" />
                    </Field>
                  )}
                </>
              )}
              <ErrorNote error={error} />
              <button className="btn btn-primary btn-block login-submit" disabled={busy}>
                {mode === 'phone' && !codeSent ? t('auth.sendCode') : t('auth.signIn')}
                <span className="login-arrow">
                  <Icon name="arrow" size={20} />
                </span>
              </button>
            </form>

            <div className="login-or">
              <span>{t('auth.or')}</span>
            </div>
            <button type="button" className="btn btn-block login-qr-btn" onClick={() => switchMode('qr')}>
              <Icon name="qr" size={22} />
              {t('auth.qrContinue')}
            </button>
          </>
        )}

        <p className="login-foot">
          <Icon name="info" size={18} />
          {isNativeApp() ? (
            <a href={nativeHome()}>{t('account.switchSchool')}</a>
          ) : tenant.kind === 'platform' ? (
            <Link to="/">{t('auth.aboutPlatform', { name: tenant.platformName })}</Link>
          ) : (
            <Link to="/">{t('welcome.back', { school: schoolName })}</Link>
          )}
        </p>
      </section>

      <ul className="login-points">
        <li>
          <span>
            <Icon name="shield" size={34} />
          </span>
          {t('auth.point1')}
        </li>
        <li>
          <span>
            <Icon name="bell" size={34} />
          </span>
          {t('auth.point2')}
        </li>
        <li>
          <span>
            <Icon name="group" size={34} />
          </span>
          {t('auth.point3')}
        </li>
      </ul>
    </div>
  );
}

interface QrStart {
  id: string;
  code: string;
  pollToken: string;
  expiresAt: string;
}

/** Shows a QR to scan with a phone that's already signed in; signs in here once it's approved there */
function QrSignIn({ onBack, onDone }: { onBack: () => void; onDone: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const [qr, setQr] = useState<QrStart | null>(null);
  const [img, setImg] = useState('');
  const [state, setState] = useState<'loading' | 'waiting' | 'expired' | 'error'>('loading');
  const [left, setLeft] = useState(0);

  const start = async () => {
    setState('loading');
    try {
      const r = await post<QrStart>('/api/auth/qr/start');
      setQr(r);
      setImg(await QRCode.toDataURL(`${window.location.origin}/qr/${r.code}`, { margin: 1, width: 480, color: { dark: '#14213D', light: '#FFFFFF' } }));
      setState('waiting');
    } catch {
      setState('error');
    }
  };
  useEffect(() => void start(), []);

  useEffect(() => {
    if (!qr || state !== 'waiting') return;
    let stopped = false;
    const tick = async () => {
      const secs = Math.max(0, Math.round((new Date(qr.expiresAt).getTime() - Date.now()) / 1000));
      setLeft(secs);
      if (secs <= 0) return setState('expired');
      try {
        const r = await post<{ status: string }>('/api/auth/qr/poll', { id: qr.id, pollToken: qr.pollToken, locale: i18n.language });
        if (stopped) return;
        if (r.status === 'approved') await onDone();
        else if (r.status === 'expired') setState('expired');
      } catch {
        /* try again on the next tick */
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 2000);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [qr, state, onDone, i18n.language]);

  return (
    <div className="login-qr">
      <h2>{t('auth.qrTitle')}</h2>
      <p className="muted">{t('auth.qrHelp')}</p>
      <div className={`login-qr-code${state === 'expired' ? ' is-expired' : ''}`}>
        {img && state !== 'error' ? <img src={img} alt={t('auth.qrTitle')} /> : <span className="muted">{state === 'error' ? t('errors.generic') : t('common.loading')}</span>}
      </div>
      {state === 'waiting' && <p className="muted small">{t('auth.qrExpires', { seconds: left })}</p>}
      {(state === 'expired' || state === 'error') && (
        <button type="button" className="btn btn-primary btn-block" onClick={() => void start()}>
          {t('auth.qrNew')}
        </button>
      )}
      <button type="button" className="btn-quiet" onClick={onBack}>
        {t('auth.qrBack')}
      </button>
    </div>
  );
}
